import React, { useEffect, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Header from "./components/layout/Header";
import Footer from "./components/layout/Footer";
import HomePage from "./pages/HomePage";
import { trackPageView } from "./lib/analytics";
import { AuthProvider, useAuth } from "./lib/AuthContext";

// Code-splitting - public pages
const HowItWorksPage = lazy(() => import("./pages/HowItWorksPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PortfolioPage = lazy(() => import("./pages/PortfolioPage"));
const TestimonialsPage = lazy(() => import("./pages/TestimonialsPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const AbonamentPage = lazy(() => import("./pages/AbonamentPage"));
const OpiekaPage = lazy(() => import("./pages/OpiekaPage"));
const SwidnicaPage = lazy(() => import("./pages/SwidnicaPage"));
const IndustryPage = lazy(() => import("./pages/IndustryPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const OrderPage = lazy(() => import("./pages/OrderPage"));
const OrderSuccessPage = lazy(() => import("./pages/OrderSuccessPage"));

// Code-splitting - auth / admin / portal
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const AdminDashboard = lazy(() => import("./pages/admin/DashboardPage"));
const AdminClients = lazy(() => import("./pages/admin/ClientsPage"));
const AdminInvoices = lazy(() => import("./pages/admin/InvoicesPage"));
const AdminTickets = lazy(() => import("./pages/admin/TicketsPage"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const PortalInvoices = lazy(() => import("./pages/portal/PortalInvoices"));
const PortalTickets = lazy(() => import("./pages/portal/PortalTickets"));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
  }, [pathname]);
  return null;
}

function LoadingFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#edf1f5]">
      <div className="text-[#2c3e50]/60 font-paragraph">Ładowanie…</div>
    </div>
  );
}

/** Route guard - redirects to /login if not authenticated with the required role. */
function ProtectedRoute({ role, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingFallback />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (role && user.role !== role) return <Navigate to={user.role === 'admin' ? '/admin' : '/portal'} replace />;
  return children;
}

/** Wrapper for public pages that include Header+Footer chrome */
function PublicLayout({ children }) {
  return (
    <div className="min-h-screen bg-[#edf1f5]">
      <Header />
      <main className="pt-16">
        {children}
      </main>
      <Footer />
    </div>
  );
}

function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* ───── Auth ───── */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/verify" element={<VerifyEmailPage />} />
        <Route path="/forgot" element={<ForgotPasswordPage />} />
        <Route path="/reset" element={<ResetPasswordPage />} />

        {/* ───── Admin panel ───── */}
        <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/clients" element={<ProtectedRoute role="admin"><AdminClients /></ProtectedRoute>} />
        <Route path="/admin/invoices" element={<ProtectedRoute role="admin"><AdminInvoices /></ProtectedRoute>} />
        <Route path="/admin/tickets" element={<ProtectedRoute role="admin"><AdminTickets /></ProtectedRoute>} />

        {/* ───── Client portal ───── */}
        <Route path="/portal" element={<ProtectedRoute role="client"><PortalDashboard /></ProtectedRoute>} />
        <Route path="/portal/invoices" element={<ProtectedRoute role="client"><PortalInvoices /></ProtectedRoute>} />
        <Route path="/portal/tickets" element={<ProtectedRoute role="client"><PortalTickets /></ProtectedRoute>} />

        {/* ───── Public pages (with Header + Footer) ───── */}
        <Route path="/" element={<PublicLayout><HomePage /></PublicLayout>} />
        <Route path="/jak-to-dziala" element={<PublicLayout><HowItWorksPage /></PublicLayout>} />
        <Route path="/cennik" element={<PublicLayout><PricingPage /></PublicLayout>} />
        <Route path="/portfolio" element={<PublicLayout><PortfolioPage /></PublicLayout>} />
        <Route path="/opinie" element={<PublicLayout><TestimonialsPage /></PublicLayout>} />
        <Route path="/faq" element={<PublicLayout><FAQPage /></PublicLayout>} />
        <Route path="/kontakt" element={<PublicLayout><ContactPage /></PublicLayout>} />

        <Route path="/blog" element={<PublicLayout><BlogPage /></PublicLayout>} />
        <Route path="/blog/:slug" element={<PublicLayout><BlogPostPage /></PublicLayout>} />

        <Route path="/strony-internetowe-w-abonamencie" element={<PublicLayout><AbonamentPage /></PublicLayout>} />
        <Route path="/opieka-nad-strona-internetowa" element={<PublicLayout><OpiekaPage /></PublicLayout>} />
        <Route path="/strony-internetowe-swidnica" element={<PublicLayout><SwidnicaPage /></PublicLayout>} />

        <Route path="/strona-dla-salonu-kosmetycznego" element={<PublicLayout><IndustryPage /></PublicLayout>} />
        <Route path="/strona-dla-lekarza" element={<PublicLayout><IndustryPage /></PublicLayout>} />
        <Route path="/strona-dla-restauracji" element={<PublicLayout><IndustryPage /></PublicLayout>} />
        <Route path="/strona-dla-firmy-budowlanej" element={<PublicLayout><IndustryPage /></PublicLayout>} />
        <Route path="/strona-dla-fotografa" element={<PublicLayout><IndustryPage /></PublicLayout>} />
        <Route path="/strona-dla-prawnika" element={<PublicLayout><IndustryPage /></PublicLayout>} />

        <Route path="/polityka-prywatnosci" element={<PublicLayout><PrivacyPolicyPage /></PublicLayout>} />
        <Route path="/regulamin" element={<PublicLayout><TermsPage /></PublicLayout>} />

        {/* Order flow (auth required - must be registered + verified client) */}
        <Route path="/zamow" element={<ProtectedRoute role="client"><OrderPage /></ProtectedRoute>} />
        <Route path="/zamow/sukces" element={<ProtectedRoute role="client"><OrderSuccessPage /></ProtectedRoute>} />

        {/* Backward-compat redirects */}
        <Route path="/how-it-works" element={<Navigate to="/jak-to-dziala" replace />} />
        <Route path="/pricing" element={<Navigate to="/cennik" replace />} />
        <Route path="/contact" element={<Navigate to="/kontakt" replace />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
